export const MAX_FIELDS = 30;
export const MAX_OPTIONS = 20;
export const MAX_PAGES = 5;
export const MAX_NOTIFICATIONS = 3;
export const MAX_NOTIFICATION_TEMPLATE = 5000;
export const MAX_FIELD_LABEL = 150;
export const MAX_PLACEHOLDER = 255;
export const MAX_FORM_NAME = 150;
export const MAX_FORM_DESCRIPTION = 5000;
export const MAX_OPTION_VALUE = 100;
export const MAX_SCHEMA_BYTES = 256 * 1024;
export const MAX_PAGE_TITLE = 150;

const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,49}$/;

export const FIELD_TYPES = [
  {
    type: "text",
    label: "Teks Pendek",
    icon: "fa fa-font",
    description: "Jawaban singkat satu baris",
    defaultLabel: "Teks Pendek",
    defaultKey: "teks_pendek",
    defaultPlaceholder: "Tulis jawaban singkat"
  },
  {
    type: "textarea",
    label: "Teks Panjang",
    icon: "fa fa-align-left",
    description: "Jawaban panjang atau catatan",
    defaultLabel: "Teks Panjang",
    defaultKey: "teks_panjang",
    defaultPlaceholder: "Tulis jawaban lengkap"
  },
  {
    type: "whatsapp",
    label: "Nomor WhatsApp",
    icon: "fa fa-whatsapp",
    description: "Nomor WhatsApp responden",
    defaultLabel: "Nomor WhatsApp",
    defaultKey: "nomor_whatsapp",
    defaultPlaceholder: "0812xxxxxxxx"
  },
  {
    type: "email",
    label: "Email",
    icon: "fa fa-envelope-o",
    description: "Alamat email responden",
    defaultLabel: "Email",
    defaultKey: "email",
    defaultPlaceholder: "nama@email.com"
  },
  {
    type: "number",
    label: "Angka",
    icon: "fa fa-sort-numeric-asc",
    description: "Jawaban berupa angka",
    defaultLabel: "Angka",
    defaultKey: "angka",
    defaultPlaceholder: "0"
  },
  {
    type: "date",
    label: "Tanggal",
    icon: "fa fa-calendar",
    description: "Pemilih tanggal",
    defaultLabel: "Tanggal",
    defaultKey: "tanggal",
    defaultPlaceholder: ""
  },
  {
    type: "radio",
    label: "Pilihan Satu Jawaban",
    icon: "fa fa-dot-circle-o",
    description: "Responden memilih satu opsi",
    defaultLabel: "Pilihan Satu Jawaban",
    defaultKey: "pilihan_satu_jawaban",
    defaultPlaceholder: ""
  },
  {
    type: "dropdown",
    label: "Dropdown",
    icon: "fa fa-list-ul",
    description: "Pilihan dari daftar dropdown",
    defaultLabel: "Dropdown",
    defaultKey: "dropdown",
    defaultPlaceholder: ""
  },
  {
    type: "checkbox",
    label: "Pilihan Beberapa Jawaban",
    icon: "fa fa-check-square-o",
    description: "Responden dapat memilih lebih dari satu",
    defaultLabel: "Pilihan Beberapa Jawaban",
    defaultKey: "pilihan_beberapa_jawaban",
    defaultPlaceholder: ""
  }
];

export const CHOICE_TYPES = ["radio", "dropdown", "checkbox"];

let uidCounter = 0;

export function createUid(prefix) {
  uidCounter += 1;
  return (
    (prefix || "id") +
    "_" +
    Date.now().toString(36) +
    "_" +
    uidCounter.toString(36)
  );
}

export function createPage(existingPages, title) {
  const pages = Array.isArray(existingPages) ? existingPages : [];
  let number = pages.length + 1;
  let id = "page_" + number;
  while (pages.some(page => page && page.id === id)) {
    number += 1;
    id = "page_" + number;
  }
  return {
    id: id,
    title: title || "Halaman " + number,
    sort_order: pages.length + 1
  };
}

export function normalizePageFromServer(rawPage, index) {
  const raw = rawPage && typeof rawPage === "object" ? rawPage : {};
  return {
    id: typeof raw.id === "string" && raw.id ? raw.id : "page_" + (index + 1),
    title: typeof raw.title === "string" ? raw.title.trim() : "",
    sort_order: Number(raw.sort_order) > 0 ? Number(raw.sort_order) : index + 1
  };
}

export function normalizePagesFromServer(rawPages, fields) {
  const pages = Array.isArray(rawPages) && rawPages.length
    ? rawPages.map(normalizePageFromServer)
    : [{ id: "page_1", title: "Halaman 1", sort_order: 1 }];
  pages.sort(function(a, b) {
    return a.sort_order - b.sort_order;
  });
  pages.forEach(function(page, index) {
    page.sort_order = index + 1;
  });
  const pageIds = pages.map(page => page.id);
  (Array.isArray(fields) ? fields : []).forEach(function(field) {
    if (!field.page_id || pageIds.indexOf(field.page_id) === -1) {
      field.page_id = pages[0].id;
    }
  });
  return pages;
}

export function buildPagePayload(page) {
  return {
    id: String(page.id || "").trim(),
    title: String(page.title || "").trim(),
    sort_order: Number(page.sort_order) || 0
  };
}

export function isChoiceType(type) {
  return CHOICE_TYPES.indexOf(type) !== -1;
}

export function getFieldTypeMeta(type) {
  for (let index = 0; index < FIELD_TYPES.length; index += 1) {
    if (FIELD_TYPES[index].type === type) {
      return FIELD_TYPES[index];
    }
  }
  return FIELD_TYPES[0];
}

export function slugifyKey(value) {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  let key = normalized || "field";
  if (!/^[a-z]/.test(key)) {
    key = "field_" + key;
  }
  key = key.replace(/_+$/g, "");
  if (!key) {
    key = "field";
  }
  return key.slice(0, 50);
}

export function makeUniqueKey(base, fields, excludeUid) {
  const normalized = slugifyKey(base);
  const list = Array.isArray(fields) ? fields : [];
  let candidate = normalized;
  let suffixNumber = 2;

  while (
    list.some(function(field) {
      if (!field || field._uid === excludeUid) {
        return false;
      }
      return String(field.key || "").trim().toLowerCase() === candidate;
    })
  ) {
    const suffix = "_" + suffixNumber;
    candidate = normalized.slice(0, 50 - suffix.length) + suffix;
    suffixNumber += 1;
  }
  return candidate;
}

export function makeUniqueOptionValue(base, options, excludeUid) {
  const normalized =
    String(base || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "opsi";
  const list = Array.isArray(options) ? options : [];
  let candidate = normalized.slice(0, 100);
  let suffixNumber = 2;

  while (
    list.some(function(option) {
      if (!option || option._uid === excludeUid) {
        return false;
      }
      return String(option.value || "").trim().toLowerCase() === candidate;
    })
  ) {
    const suffix = "_" + suffixNumber;
    candidate = normalized.slice(0, 100 - suffix.length) + suffix;
    suffixNumber += 1;
  }
  return candidate;
}

export function createOption(label, value) {
  return {
    _uid: createUid("opt"),
    value: value || "",
    label: label || ""
  };
}

export function createDefaultOptions() {
  return [createOption("Opsi 1", "opsi_1"), createOption("Opsi 2", "opsi_2")];
}

export function createField(type, existingFields, overrides) {
  const meta = getFieldTypeMeta(type);
  const field = {
    _uid: createUid("field"),
    key: "",
    type: meta.type,
    label: meta.defaultLabel,
    required: false,
    placeholder: meta.defaultPlaceholder || "",
    options: [],
    page_id: ""
  };

  if (isChoiceType(meta.type)) {
    field.options = createDefaultOptions();
  }

  if (overrides && typeof overrides === "object") {
    Object.keys(overrides).forEach(function(keyName) {
      if (keyName !== "_uid") {
        field[keyName] = overrides[keyName];
      }
    });
  }

  field.key = makeUniqueKey(
    overrides && overrides.key ? overrides.key : meta.defaultKey,
    existingFields,
    field._uid
  );
  return field;
}

export function normalizeFieldFromServer(rawField) {
  const raw = rawField && typeof rawField === "object" ? rawField : {};
  const type = getFieldTypeMeta(raw.type).type;
  const field = {
    _uid: createUid("field"),
    key: typeof raw.key === "string" ? raw.key : "",
    type: type,
    label: typeof raw.label === "string" ? raw.label : "",
    required: raw.required === true,
    placeholder: typeof raw.placeholder === "string" ? raw.placeholder : "",
    options: [],
    page_id: typeof raw.page_id === "string" ? raw.page_id : ""
  };

  if (isChoiceType(type) && Array.isArray(raw.options)) {
    field.options = raw.options.map(function(option) {
      const data = option && typeof option === "object" ? option : {};
      return {
        _uid: createUid("opt"),
        value: typeof data.value === "string" ? data.value : "",
        label: typeof data.label === "string" ? data.label : ""
      };
    });
  }

  return field;
}

export function buildFieldPayload(field) {
  const payload = {
    key: String(field.key || "").trim(),
    type: field.type,
    label: String(field.label || "").trim(),
    required: field.required === true
  };

  if (field.page_id) {
    payload.page_id = String(field.page_id).trim();
  }

  if (isChoiceType(field.type)) {
    payload.options = (Array.isArray(field.options) ? field.options : []).map(
      function(option) {
        return {
          value: String(option.value || "").trim(),
          label: String(option.label || "").trim()
        };
      }
    );
  } else {
    const placeholder = String(field.placeholder || "").trim();
    if (placeholder) {
      payload.placeholder = placeholder;
    }
  }

  return payload;
}

export function buildFormPayload(options) {
  const data = options || {};
  const payload = {};

  if (data.id) {
    payload.id = data.id;
  }
  payload.name = String(data.name || "").trim();
  payload.description = String(data.description || "").trim();

  if (data.includeFields !== false) {
    payload.fields = (Array.isArray(data.fields) ? data.fields : []).map(
      buildFieldPayload
    );
    if (Array.isArray(data.pages)) {
      payload.pages = data.pages.map(buildPagePayload);
    }
  }

  return payload;
}

export function validateForm(options) {
  const data = options || {};
  const includeFields = data.includeFields !== false;
  const fields = Array.isArray(data.fields) ? data.fields : [];
  const errors = {
    name: "",
    description: "",
    general: "",
    pages: "",
    fields: {}
  };

  const name = String(data.name || "").trim();
  if (!name) {
    errors.name = "Nama form wajib diisi.";
  } else if (name.length > MAX_FORM_NAME) {
    errors.name = "Nama form maksimal 150 karakter.";
  }

  const description = String(data.description || "");
  if (description.length > MAX_FORM_DESCRIPTION) {
    errors.description = "Deskripsi maksimal 5000 karakter.";
  }

  if (!includeFields) {
    return errors;
  }

  if (fields.length === 0) {
    errors.general = "Tambahkan minimal 1 field sebelum menyimpan form.";
  } else if (fields.length > MAX_FIELDS) {
    errors.general = "Maksimal 30 field dalam satu form.";
  }

  const pages = Array.isArray(data.pages) ? data.pages : [];
  if (pages.length > MAX_PAGES) {
    errors.pages = "Maksimal 5 halaman per form.";
  } else if (pages.length) {
    const pageIds = {};
    pages.forEach(function(page, index) {
      const id = String(page.id || "").trim();
      const title = String(page.title || "").trim();
      if (!id) {
        errors.pages = "Setiap halaman harus memiliki ID.";
      } else if (pageIds[id]) {
        errors.pages = "ID halaman harus unik.";
      } else if (Number(page.sort_order) !== index + 1) {
        errors.pages = "Urutan halaman harus berurutan 1 sampai 5.";
      } else if (title.length > MAX_PAGE_TITLE) {
        errors.pages = "Judul halaman maksimal 150 karakter.";
      }
      pageIds[id] = true;
    });
    const pageIdList = pages.map(page => page.id);
    fields.forEach(function(field) {
      if (!field.page_id || pageIdList.indexOf(field.page_id) === -1) {
        errors.general = "Setiap field harus terkait dengan halaman yang valid.";
      }
    });
  }

  const keyCounts = {};
  fields.forEach(function(field) {
    const key = String(field.key || "").trim().toLowerCase();
    if (key) {
      keyCounts[key] = (keyCounts[key] || 0) + 1;
    }
  });

  fields.forEach(function(field) {
    const fieldError = {
      label: "",
      key: "",
      placeholder: "",
      options: "",
      optionErrors: {}
    };

    const label = String(field.label || "").trim();
    if (!label) {
      fieldError.label = "Label field wajib diisi.";
    } else if (label.length > MAX_FIELD_LABEL) {
      fieldError.label = "Label field maksimal 150 karakter.";
    }

    const key = String(field.key || "").trim();
    if (!key) {
      fieldError.key = "Key field wajib diisi.";
    } else if (!KEY_PATTERN.test(key)) {
      fieldError.key =
        "Key harus diawali huruf dan hanya berisi huruf, angka, atau garis bawah (maks. 50).";
    } else if (keyCounts[key.toLowerCase()] > 1) {
      fieldError.key = "Key field harus unik.";
    }

    if (isChoiceType(field.type)) {
      const options = Array.isArray(field.options) ? field.options : [];
      if (options.length < 1) {
        fieldError.options = "Tambahkan minimal 1 pilihan.";
      } else if (options.length > MAX_OPTIONS) {
        fieldError.options = "Maksimal 20 pilihan per field.";
      }

      const valueCounts = {};
      options.forEach(function(option) {
        const value = String(option.value || "").trim().toLowerCase();
        if (value) {
          valueCounts[value] = (valueCounts[value] || 0) + 1;
        }
      });

      options.forEach(function(option) {
        const optionError = { value: "", label: "" };
        const value = String(option.value || "");
        const optionLabel = String(option.label || "").trim();

        if (!value.trim()) {
          optionError.value = "Nilai pilihan wajib diisi.";
        } else if (value !== value.trim()) {
          optionError.value =
            "Nilai pilihan tidak boleh diawali atau diakhiri spasi.";
        } else if (value.length > MAX_OPTION_VALUE) {
          optionError.value = "Nilai pilihan maksimal 100 karakter.";
        } else if (valueCounts[value.trim().toLowerCase()] > 1) {
          optionError.value = "Nilai pilihan harus unik.";
        }

        if (!optionLabel) {
          optionError.label = "Teks pilihan wajib diisi.";
        } else if (optionLabel.length > MAX_FIELD_LABEL) {
          optionError.label = "Teks pilihan maksimal 150 karakter.";
        }

        if (optionError.value || optionError.label) {
          fieldError.optionErrors[option._uid] = optionError;
        }
      });
    } else {
      const placeholder = String(field.placeholder || "");
      if (placeholder.length > MAX_PLACEHOLDER) {
        fieldError.placeholder = "Placeholder maksimal 255 karakter.";
      }
    }

    errors.fields[field._uid] = fieldError;
  });

  if (!errors.general) {
    try {
      const serialized = JSON.stringify(fields.map(buildFieldPayload));
      if (serialized.length > MAX_SCHEMA_BYTES) {
        errors.general = "Total schema field terlalu besar untuk disimpan.";
      }
    } catch (error) {
      errors.general = "Schema field tidak dapat diproses.";
    }
  }

  return errors;
}

export function hasValidationErrors(errors) {
  if (!errors) {
    return false;
  }
  if (errors.name || errors.description || errors.general || errors.pages) {
    return true;
  }
  const fieldErrors = errors.fields || {};
  return Object.keys(fieldErrors).some(function(uid) {
    const fieldError = fieldErrors[uid];
    if (!fieldError) {
      return false;
    }
    if (
      fieldError.label ||
      fieldError.key ||
      fieldError.placeholder ||
      fieldError.options
    ) {
      return true;
    }
    const optionErrors = fieldError.optionErrors || {};
    return Object.keys(optionErrors).some(function(optionUid) {
      const optionError = optionErrors[optionUid];
      return !!(optionError && (optionError.value || optionError.label));
    });
  });
}



// WEBPACK FOOTER //
// ./src/utils/waajoFormsSchema.js